import { Icon } from "@nova/protobuf"
import JSZip from "jszip"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { getIconUrl } from "@nova/component-map-icons"
import { escapeXmlEntities } from "@nova/util-strings"

export type IconAssets = {
    loadSvg: (assetPath: string) => Promise<string>
    encodePng: (svgContent: string, sizeInPixels: number) => Promise<Uint8Array>
}

const iconSizeInPixels = 32

export const buildIconKMZ = async (assets: IconAssets, icons: Icon[]) => {
    const zip = new JSZip()

    const kml = `<?xml version="1.0" encoding="UTF-8"?>
    <kml xmlns="http://www.opengis.net/kml/2.2" xmlns:atom="http://www.w3.org/2005/Atom" xmlns:gx="http://www.google.com/kml/ext/2.2" xmlns:kml="http://www.opengis.net/kml/2.2">
    <Document>
    ${iconsToPlacemarks(icons)}
    ${await iconsToStyles(assets, icons, zip)}
    </Document>
</kml>`

    zip.file(`icons-${Date.now()}.kml`, kml)
    return await zip.generateAsync({ type: "uint8array" })
}

const iconsToPlacemarks = (icons: Icon[]) => {
    return icons
        .map(
            (icon) => `<Placemark>
      <name>${escapeXmlEntities(icon.name)}</name>
      <styleUrl>#${getIconStyleId(icon)}</styleUrl>
      <Point>
        <coordinates>${icon.longitude},${icon.latitude},0</coordinates>
      </Point>
    </Placemark>`
        )
        .join("\n")
}

const iconsToStyles = async (assets: IconAssets, icons: Icon[], zip: JSZip) => {
    const map = icons.reduce(
        (acc, curr) => {
            acc[getIconStyleId(curr)] = curr
            return acc
        },
        {} as Record<string, Icon>
    )

    const stylesArray: string[] = []
    await Promise.all(
        Object.entries(map).map(async ([key, icon]) => {
            const png = await buildIconPng(assets, icon)
            if (!png) return

            const pngFilename = `${getIconStyleId(icon)}.png`
            zip.file(pngFilename, png)

            stylesArray.push(`<Style id="${key}">
      <IconStyle>
        <scale>1</scale>
        <Icon>
          <href>${pngFilename}</href>
        </Icon>
        <hotSpot x="16" y="16" xunits="pixels" yunits="pixels"/>
      </IconStyle>
    </Style>`)
        })
    )
    return stylesArray.join("\n")
}

export const buildIconPng = async (assets: IconAssets, icon: Pick<Icon, "url" | "color">) => {
    const assetPath = getIconAssetPath(icon)
    if (!assetPath) return

    const svgContent = await assets.loadSvg(assetPath)
    const colored =
        isShapeIcon(icon) && icon.color
            ? modifySVGColor(svgContent, decodeNumberToColor(icon.color))
            : svgContent

    return await assets.encodePng(colored, iconSizeInPixels)
}

const getIconAssetPath = (icon: Pick<Icon, "url">) => {
    const path = icon.url
        .replace("/images/icons/wildfire/basic", "fireai_wildfire")
        .replace(".svg", "")
    return getIconUrl(path) || getIconUrl("fireai_" + path)
}

const getIconStyleId = (icon: Icon) => `${icon.url.replace("/", "-")}-${icon.color}`

const modifySVGColor = (svgContent: string, newColor: string) => {
    const rootStart = svgContent.indexOf("<svg")
    if (rootStart < 0) return svgContent

    const contentStart = svgContent.indexOf(">", rootStart) + 1
    const contentEnd = svgContent.lastIndexOf("</svg>")
    if (contentStart === 0 || contentEnd < contentStart) return svgContent

    return [
        svgContent.slice(0, contentStart),
        `<g fill="${newColor}">`,
        svgContent.slice(contentStart, contentEnd),
        "</g>",
        svgContent.slice(contentEnd),
    ].join("")
}

const isShapeIcon = (icon: Pick<Icon, "url">) => icon.url.split("/").at(0) === "shape"
